import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import HeadingSummaryBlockStyles from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled, { css } from 'styled-components';
export const colourDefinitions = css `
  /* Primary Colours */
  &[data-primary=${BrandColours.BlueDark}] {
    --primary: ${brand.primary.blue.dark};
  }

  &[data-primary=${BrandColours.BlueLight}] {
    --primary: ${brand.primary.blue.light};
  }

  &[data-primary=${BrandColours.GreenDark}] {
    --primary: ${brand.secondary.green.dark};
  }

  &[data-primary=${BrandColours.GreenLight}] {
    --primary: ${brand.secondary.green.light};
  }

  &[data-primary=${BrandColours.GreyPrimary}] {
    --primary: ${brand.primary.grey};
  }

  &[data-primary=${BrandColours.OrangeDark}] {
    --primary: ${brand.secondary.orange.dark};
  }

  &[data-primary=${BrandColours.OrangeLight}] {
    --primary: ${brand.secondary.orange.light};
  }

  &[data-primary=${BrandColours.PinkDark}] {
    --primary: ${brand.secondary.pink.dark};
  }

  &[data-primary=${BrandColours.PinkLight}] {
    --primary: ${brand.secondary.pink.light};
  }

  &[data-primary=${BrandColours.PurpleDark}] {
    --primary: ${brand.primary.purple.dark};
  }

  &[data-primary=${BrandColours.PurpleLight}] {
    --primary: ${brand.primary.purple.light};
  }

  &[data-primary=${BrandColours.White}] {
    --primary: ${brand.global.white};
  }

  /* Secondary Colours */
  &[data-secondary=${BrandColours.BlueDark}] {
    --secondary: ${brand.primary.blue.dark};
  }

  &[data-secondary=${BrandColours.BlueLight}] {
    --secondary: ${brand.primary.blue.light};
  }

  &[data-secondary=${BrandColours.GreenDark}] {
    --secondary: ${brand.secondary.green.dark};
  }

  &[data-secondary=${BrandColours.GreenLight}] {
    --secondary: ${brand.secondary.green.light};
  }

  &[data-secondary=${BrandColours.GreyPrimary}] {
    --secondary: ${brand.primary.grey};
  }

  &[data-secondary=${BrandColours.OrangeDark}] {
    --secondary: ${brand.secondary.orange.dark};
  }

  &[data-secondary=${BrandColours.OrangeLight}] {
    --secondary: ${brand.secondary.orange.light};
  }

  &[data-secondary=${BrandColours.PinkDark}] {
    --secondary: ${brand.secondary.pink.dark};
  }

  &[data-secondary=${BrandColours.PinkLight}] {
    --secondary: ${brand.secondary.pink.light};
  }

  &[data-secondary=${BrandColours.PurpleDark}] {
    --secondary: ${brand.primary.purple.dark};
  }

  &[data-secondary=${BrandColours.PurpleLight}] {
    --secondary: ${brand.primary.purple.light};
  }

  &[data-secondary=${BrandColours.White}] {
    --secondary: ${brand.global.white};
  }

  /* Tertiary Colours */
  &[data-tertiary=${BrandColours.BlueDark}] {
    --tertiary: ${brand.primary.blue.dark};
  }

  &[data-tertiary=${BrandColours.BlueLight}] {
    --tertiary: ${brand.primary.blue.light};
  }

  &[data-tertiary=${BrandColours.GreenDark}] {
    --tertiary: ${brand.secondary.green.dark};
  }

  &[data-tertiary=${BrandColours.GreenLight}] {
    --tertiary: ${brand.secondary.green.light};
  }

  &[data-tertiary=${BrandColours.GreyPrimary}] {
    --tertiary: ${brand.primary.grey};
  }

  &[data-tertiary=${BrandColours.OrangeDark}] {
    --tertiary: ${brand.secondary.orange.dark};
  }

  &[data-tertiary=${BrandColours.OrangeLight}] {
    --tertiary: ${brand.secondary.orange.light};
  }

  &[data-tertiary=${BrandColours.PinkDark}] {
    --tertiary: ${brand.secondary.pink.dark};
  }

  &[data-tertiary=${BrandColours.PinkLight}] {
    --tertiary: ${brand.secondary.pink.light};
  }

  &[data-tertiary=${BrandColours.PurpleDark}] {
    --tertiary: ${brand.primary.purple.dark};
  }

  &[data-tertiary=${BrandColours.PurpleLight}] {
    --tertiary: ${brand.primary.purple.light};
  }

  &[data-tertiary=${BrandColours.White}] {
    --tertiary: ${brand.global.white};
  }
`;
const Container = styled(SectionWrapper) `
  ${colourDefinitions};

  --primary: ${brand.primary.purple.light};
  --secondary: ${brand.global.white};
  --tertiary: ${brand.primary.blue.light};
  --sectionBackground: transparent;
  --sectionColour: ${brand.global.black};
  --paddingY: ${fluid(40, 68)};
  --rowGap: ${fluid(24, 48)};

  position: relative;
  overflow: hidden;

  &[data-primary=${BrandColours.BlueDark}],
  &[data-primary=${BrandColours.GreenDark}],
  &[data-primary=${BrandColours.OrangeDark}],
  &[data-primary=${BrandColours.PinkDark}],
  &[data-primary=${BrandColours.PurpleDark}],
  &[data-primary=${BrandColours.PurpleLight}] {
    --sectionColour: ${brand.global.white};
  }

  &[data-layout='centre-text'] {
    ${SectionWrapperStyles.Inner} {
      min-height: 424px;
      text-align: center;

      ${HeadingSummaryBlockStyles.Container} {
        margin: 0 auto;
      }

      @media ${from(Device.TabletLarge)} {
        min-height: 346px;
        justify-content: center;
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    &:not(:has(img))::before {
      ${transition('opacity')}

      content: '';
      position: absolute;
      top: 0;
      left: 0;
      background-color: var(--primary);
      height: 100%;
      width: 50%;
      z-index: -1;
      transition-delay: 350ms;
    }
  }

  ${SectionWrapperStyles.Inner} {
    display: grid;
    gap: var(--rowGap);
    padding-top: var(--paddingY);
    padding-bottom: var(--paddingY);

    @media ${from(Device.TabletLarge)} {
      min-height: 513px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-layout='contained-image'] {
      ${SectionWrapperStyles.Inner} {
        min-height: ${fluid(324, 513, Device.TabletLarge, Device.DesktopLarge)};
      }
    }
  }

  &[data-animate='false'] {
    &::before {
      opacity: 0;
    }
  }

  ${HeadingSummaryBlockStyles.Container} {
    ${transition('transform, opacity', 0.5)};

    transition-delay: 0.25s;

    @media ${until(Device.TabletLarge)} {
      grid-row: 2 / span 1;
      grid-column: 1 / span 1;
    }

    @media ${from(Device.TabletLarge)} {
      width: 40%;
      max-width: 592px;
    }

    ${ButtonStyles.Container} {
      --colourTextHover: var(--primary) !important;
    }
  }

  ${HeadingSummaryBlockStyles.Container}[data-animate='false'] {
    opacity: 0;
    transform: translateY(20px);
  }

  ${HeadingSummaryBlockStyles.Content},
  ${HeadingSummaryBlockStyles.Heading},
  ${HeadingSummaryBlockStyles.Subheading} {
    background-color: var(--primary);
    color: var(--sectionColour);
  }

  ${HeadingSummaryBlockStyles.Content} {
    p {
      font-size: ${fluid(14, 20)};
      line-height: ${fluid(22, 28)};
    }
  }

  ${HeadingSummaryBlockStyles.ButtonWrapper} {
    gap: ${fluid(12, 16)};
    margin-top: 14px;
  }
`;
const ImageWrapper = styled.div `
  ${imageWrapperPreserveRatio(344, 200)};

  @media ${until(Device.TabletLarge)} {
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
  }

  ${Container}[data-layout='contained-image'] & {
    @media ${from(Device.TabletLarge)} {
      aspect-ratio: 695 / 376;
      width: 40%;
      max-width: 695px;
    }
  }

  ${Container}[data-layout='background-image'] & {
    margin: calc(var(--paddingY) * -1) calc(var(--sitePadding) * -1) 0 calc(var(--sitePadding) * -1);

    @media ${until(Device.TabletLarge)} {
      width: calc(100% + var(--sitePadding) * 2);
      z-index: -2;
    }

    @media ${from(Device.TabletLarge)} {
      display: contents;
    }
  }
`;
const Image = styled.img `
  ${transition('opacity', 0.5)};
  ${imageCoverContainer()};

  transition-delay: 0.15s;

  ${Container}[data-animate='false'] & {
    opacity: 0;
  }

  ${Container}[data-layout='contained-image'] & {
    border-radius: ${fluid(20, 26)};
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-layout='background-image'] & {
      width: 80%;
      right: 0;
      left: auto;
      z-index: -2;
    }
  }
`;
const PrimaryBackground = styled.div `
  ${transition('transform', 0.5)};

  --xAdjust: -40%;
  --yAdjust: 96px;

  ${Container}[data-layout='background-image'] & {
    --xAdjust: -47.5%;
    --yAdjust: ${fluid(64, 146, Device.MobileLarge, Device.TabletLarge)};
  }

  grid-row: 2 / span 1;
  grid-column: 1 / span 1;
  position: relative;
  z-index: -1;
  background-color: var(--primary);
  width: 150%;
  height: calc(150% + var(--yAdjust) + var(--rowGap));
  border-radius: 0 50px 0 0;
  transform: rotate(-7deg);
  transform-origin: top right;
  margin: calc((var(--yAdjust) * -1) - var(--rowGap)) 0 0 var(--xAdjust);

  ${Container}[data-primary=${BrandColours.BlueDark}] & {
    --primary: ${brand.primary.blue.dark};
  }

  ${Container}[data-animate='false'] & {
    transform: rotate(0) !important;
  }

  @media ${until(Device.TabletLarge)} {
    ${Container}:not(:has(${ImageWrapper})) & {
      position: absolute;
      top: 0;
      left: 0;
      margin: 0;
      transform: translate(-15%, -10%) rotate(-45deg);
      width: 200%;
      transform-origin: center;
      aspect-ratio: 1;
      height: auto;
    }

    ${Container}[data-layout='centre-text'] & {
      transform: translate(-20%, 12.5%) rotate(-45deg);
    }
  }

  @media ${from(Device.TabletLarge)} {
    position: absolute;
    bottom: 0;
    left: 0;
    border-radius: 0 0 66px 0;
    transform: rotate(-15deg);
    transform-origin: bottom center;
    width: 50%;
    height: 300%;
    transition-duration: 650ms;
    margin: 0;

    ${Container}:not(:has(${ImageWrapper})) & {
      transition-duration: 600ms;
      width: 70vw;
      transform: translateY(-50%) rotate(-45deg);
      height: 70vw;
      transform-origin: center;
      top: 50%;
    }

    ${Container}[data-layout='centre-text'] & {
      width: 66.667vw;
      height: 66.667vw;
    }
  }
`;
const SecondaryBackground = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -2;
  background-color: var(--secondary);
`;
const TertiaryBackground = styled.div `
  ${transition('transform', 0.5)};

  position: absolute;
  background-color: var(--tertiary);
  width: 60%;
  right: 0;
  top: 0;
  border-radius: 0 50px 0 0;
  aspect-ratio: 0.35;
  mix-blend-mode: multiply;
  transition-delay: 350ms;
  transform: rotate(-45deg) translate(150%, -10%);

  ${Container}[data-layout='centre-text'] & {
    border-top-left-radius: 50px;
    transform: rotate(-45deg) translate(135%, -10%);
  }

  ${Container}[data-animate='false'] & {
    transform: rotate(-45deg) translate(250%, -10%);
  }

  @media ${until(Device.TabletLarge)} {
    z-index: 2;
  }

  @media ${from(Device.TabletLarge)} {
    transform: rotate(-45deg) translate(55%, 25%);

    ${Container}[data-animate='false'] & {
      transform: rotate(-45deg) translate(-200%, 25%);
    }

    ${Container}[data-layout='centre-text'] & {
      width: 40%;
      transform: rotate(-55deg) translate(65%, 25%) skew(0deg, 10deg);
    }

    ${Container}[data-layout='centre-text'][data-animate='false'] & {
      transform: rotate(-55deg) translate(-100%, 10%) skew(0deg, 10deg);
    }
  }

  @media ${from(Device.Desktop)} {
    width: 40%;
    transform: rotate(-45deg) translate(46%, 25%);

    ${Container}[data-layout='centre-text'] & {
      width: 30%;
      transform: rotate(-55deg) translate(55%, 19%) skew(0deg, 10deg);
    }
  }

  @media ${from(Device.DesktopLarge)} {
    transform: rotate(-45deg) translate(55%, 22%);

    ${Container}[data-layout='centre-text'] & {
      width: 27.5%;
      transform: rotate(-55deg) translate(45%, 16%) skew(0deg, 10deg);
    }
  }
`;
const InnerPageHeroStyles = {
    Container,
    Image,
    ImageWrapper,
    PrimaryBackground,
    SecondaryBackground,
    TertiaryBackground,
};
export default InnerPageHeroStyles;
